import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { INoticeViolation, NoticeViolation } from '../models/notice-violation.model';
import { AuthService } from './auth.service';
import { AwsService } from './aws.service';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { IPresignResponse } from '../models/presign-response.model';
import { IFileMedia } from '../models/file-media.model';
import { IUploadPosition } from '../models/upload-progress.model';

@Injectable({
  providedIn: 'root'
})
export class NoticeViolationService {

  /** Tope de peso de un PDF (el API lo exige igual: 3 MB). */
  public readonly maxPdfSizeMB: number = 3;

  public loading: boolean = false;
  public noticeViolations: NoticeViolation[] = [];

  constructor(
    public authService: AuthService,
    public awsService: AwsService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR request                               */
  /* -------------------------------------------------------------------------- */
  /* Un solo punto para las llamadas HTTP del módulo: arma los headers con el
     token y, cuando el API contesta con su envelope de error (400, 404...),
     lo devuelve como ServerResponse en vez de lanzar. */
  private async request<T = any>(
    method: 'get' | 'post' | 'put' | 'delete',
    urlApi: string,
    body?: object
  ): Promise<ServerResponse<T>> {

    const token = await this.authService.getToken();

    const options = {
      headers: {
        'content-type': 'application/json',
        'Authorization': token.toString()
      }
    };

    return new Promise<ServerResponse<T>>(
      (resolve, reject) => {

        let observable;

        if (method === 'get') {
          observable = this.http.get<IServerResponse<T>>(urlApi, options);
        } else if (method === 'delete') {
          observable = this.http.delete<IServerResponse<T>>(urlApi, options);
        } else if (method === 'put') {
          observable = this.http.put<IServerResponse<T>>(urlApi, JSON.stringify(body ?? {}), options);
        } else {
          observable = this.http.post<IServerResponse<T>>(urlApi, JSON.stringify(body ?? {}), options);
        }

        observable.subscribe({
          next: (response: IServerResponse<T>) => resolve(
            new ServerResponse<T>(response)
          ),
          error: (error) => {
            if (error.error) {
              resolve(new ServerResponse<T>(error.error));
            } else {
              reject(error);
            }
          }
        });

      }
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR showSuccess                             */
  /* -------------------------------------------------------------------------- */
  private showSuccess(message: string) {
    this.toastService.show({
      body: `${message}`,
      classname: 'bg-success text-white'
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR showError                              */
  /* -------------------------------------------------------------------------- */
  private showError(message: string) {
    this.toastService.show({
      header: 'Error',
      body: `${message}`,
      classname: 'bg-danger text-white'
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR create                                */
  /* -------------------------------------------------------------------------- */
  // region create
  async create(
    neighborhoodId: string,
    noticeViolation: {
      houseFlatId: string;
      title: string;
      description: string;
    }
  ): Promise<NoticeViolation | null> {

    let noticeViolationCreated: NoticeViolation | null = null;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/notice-violations`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando amonestación...';

    try {

      const response = await this.request<{ noticeViolation: INoticeViolation }>(
        'post',
        urlApi,
        noticeViolation
      );

      if (response.statusCode === 201) {

        noticeViolationCreated = new NoticeViolation(
          response.data!.noticeViolation
        );

        this.showSuccess(response.message);

      } else {
        this.showError(response.message);
      }

    } catch (error) {
      this.showError(`${error}`);
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return noticeViolationCreated;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR delete                                */
  /* -------------------------------------------------------------------------- */
  // region delete
  async delete(
    noticeViolation: NoticeViolation
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${noticeViolation.neighborhoodId}/notice-violations/${noticeViolation.noticeViolationId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando amonestación...';

    try {

      const response = await this.request('delete', urlApi);

      if (response.statusCode === 200) {

        flag = true;
        this.showSuccess(response.message);

        this.getAll(
          noticeViolation.neighborhoodId
        );

      } else {
        this.showError(response.message);
      }

    } catch (error) {
      this.showError(`${error}`);
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR deleteFile                             */
  /* -------------------------------------------------------------------------- */
  // region deleteFile
  async deleteFile(
    noticeViolation: NoticeViolation,
    fileId: string,
    silent: boolean = false
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${noticeViolation.neighborhoodId}/notice-violations/${noticeViolation.noticeViolationId}/files/${fileId}`;

    if (!silent) {
      this.spinnerService.loading = true;
      this.spinnerService.message = 'Eliminando archivo...';
    }

    try {

      const response = await this.request('delete', urlApi);

      if (response.statusCode === 200) {

        flag = true;

        if (!silent) {
          this.showSuccess(response.message);
        }

      } else if (!silent) {
        this.showError(response.message);
      }

    } catch (error) {
      if (!silent) {
        this.showError(`${error}`);
      }
    }

    if (!silent) {
      this.spinnerService.loading = false;
      this.spinnerService.message = '';
    }

    return flag;
  }

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR get                                 */
  /* -------------------------------------------------------------------------- */
  // region get
  async get(
    neighborhoodId: string,
    noticeViolationId: string
  ): Promise<NoticeViolation | null> {

    let noticeViolation: NoticeViolation | null = null;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/notice-violations/${noticeViolationId}`;

    try {

      const response = await this.request<{ noticeViolation: INoticeViolation }>(
        'get',
        urlApi
      );

      if (response.statusCode === 200) {
        noticeViolation = new NoticeViolation(
          response.data!.noticeViolation
        );
      } else {
        this.showError(response.message);
      }

    } catch (error) {
      this.showError(`${error}`);
    }

    return noticeViolation;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR getAll                                */
  /* -------------------------------------------------------------------------- */
  // region getAll
  async getAll(
    neighborhoodId: string
  ) {

    this.loading = true;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/notice-violations`;

    try {

      const response = await this.request<{ noticeViolations: INoticeViolation[] }>(
        'get',
        urlApi
      );

      if (response.statusCode === 200) {
        this.noticeViolations = response.data!.noticeViolations.map(
          (item) => new NoticeViolation(item)
        );
      } else {
        this.showError(response.message);
      }

    } catch (error) {
      this.showError(`${error}`);
    }

    this.loading = false;
  }

  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR getByHouseFlat                            */
  /* -------------------------------------------------------------------------- */
  // region getByHouseFlat
  async getByHouseFlat(
    neighborhoodId: string,
    streetBuildingId: string,
    houseFlatId: string
  ): Promise<NoticeViolation[]> {

    let noticeViolations: NoticeViolation[] = [];

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/streets-buildings/${streetBuildingId}/houses-flats/${houseFlatId}/notice-violations`;

    try {

      const response = await this.request<{ noticeViolations: INoticeViolation[] }>(
        'get',
        urlApi
      );

      if (response.statusCode === 200) {
        noticeViolations = response.data!.noticeViolations.map(
          (item) => new NoticeViolation(item)
        );
      } else {
        this.showError(response.message);
      }

    } catch (error) {
      this.showError(`${error}`);
    }

    return noticeViolations;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR update                                */
  /* -------------------------------------------------------------------------- */
  // region update
  async update(
    noticeViolation: NoticeViolation
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${noticeViolation.neighborhoodId}/notice-violations/${noticeViolation.noticeViolationId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando amonestación...';

    try {

      const response = await this.request(
        'put',
        urlApi,
        {
          title: noticeViolation.title,
          description: noticeViolation.description,
          status: noticeViolation.status
        }
      );

      if (response.statusCode === 200) {

        flag = true;
        this.showSuccess(response.message);

        this.getAll(
          noticeViolation.neighborhoodId
        );

      } else {
        this.showError(response.message);
      }

    } catch (error) {
      this.showError(`${error}`);
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR isValidPdf                              */
  /* -------------------------------------------------------------------------- */
  // region isValidPdf
  /** Valida en el cliente lo mismo que exige el API: solo PDF y de máximo 3 MB. */
  isValidPdf(
    file: File
  ): boolean {

    if (file.type !== 'application/pdf') {
      this.showError(`El archivo "${file.name}" no es un PDF.`);
      return false;
    }

    if (file.size > this.maxPdfSizeMB * 1024 * 1024) {
      this.showError(
        `El PDF "${file.name}" pesa más de ${this.maxPdfSizeMB} MB. Suba un archivo más ligero.`
      );
      return false;
    }

    return true;
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR uploadFile                              */
  /* -------------------------------------------------------------------------- */
  // region uploadFile
  /** Registra el archivo en el API y sube el blob a la liga prefirmada.
      Acepta un File (PDF) o un data URL base64 (imagen ya comprimida).
      fileName y position son lo que el spinner muestra mientras sube
      ("Foto 2", "Archivo 2 de 5"); sin fileName se usa el name del File. */
  async uploadFile(
    noticeViolation: NoticeViolation,
    file: File | string,
    fileName?: string,
    position: IUploadPosition | null = null
  ): Promise<boolean> {

    let flag = false;

    const name = fileName ?? (
      typeof file === 'string' ? 'Imagen' : file.name
    );

    let uploadBlob: Blob;
    let contentType: string;

    if (typeof file === 'string') {
      const match = file.match(/^data:(.+?);base64,/);
      contentType = match ? match[1] : 'application/octet-stream';
      const byteString = atob(file.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      uploadBlob = new Blob([ab], { type: contentType });
    } else {
      uploadBlob = file;
      contentType = file.type;
    }

    const isPdf = contentType === 'application/pdf';

    if (!isPdf && !contentType.startsWith('image/')) {
      this.showError('Solo se permiten imágenes o archivos PDF.');
      return false;
    }

    if (isPdf && uploadBlob.size > this.maxPdfSizeMB * 1024 * 1024) {
      this.showError(
        `El PDF pesa más de ${this.maxPdfSizeMB} MB. Suba un archivo más ligero.`
      );
      return false;
    }

    const urlApi = `${environment.urlApi}/neighborhoods/${noticeViolation.neighborhoodId}/notice-violations/${noticeViolation.noticeViolationId}/files`;

    this.spinnerService.loading = true;
    this.spinnerService.startUpload(name, position);

    try {

      const response = await this.request<{
        file: IFileMedia;
        presignFile: IPresignResponse;
      }>(
        'post',
        urlApi,
        {
          contentType,
          size: uploadBlob.size
        }
      );

      if (response.statusCode === 201) {

        const uploaded = await this.awsService.uploadFile(
          response.data!.presignFile.uploadUrl,
          uploadBlob,
          contentType,
          name,
          position
        );

        if (uploaded) {

          flag = true;
          this.showSuccess(response.message);

        } else {

          /* El registro ya existe en el API pero el archivo nunca llegó a S3:
             se borra para no dejar un archivo roto en la amonestación. */
          await this.deleteFile(
            noticeViolation,
            response.data!.file.fileId,
            true
          );

          this.showError('No se pudo subir el archivo.');

        }

      } else {
        this.showError(response.message);
      }

    } catch (error) {
      console.error(error);
      this.showError(`${error}`);
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    this.spinnerService.clearUpload();

    return flag;
  }

}
